import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, PackageX, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Skeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { errorMessage, useToast } from '../../lib/toast';
import type { AppKey, Platform, VersionBlock } from '../../lib/types';

const APPS: { key: AppKey; label: string }[] = [
  { key: 'dualchat', label: 'InvisChat' },
  { key: 'ig_saver', label: 'IG Saver' },
  { key: 'whatsi_saver', label: 'Whatsi Saver' },
];

const APP_LABELS: Record<AppKey, string> = {
  dualchat: 'InvisChat',
  ig_saver: 'IG Saver',
  whatsi_saver: 'Whatsi Saver',
};

/**
 * Builds that must not be pushed to.
 *
 * The blacklist next door silences people; this silences a shipped build —
 * the one whose users would crash on the payload, or land on a screen it
 * doesn't have yet. Enforced at the same single point every push in the
 * backend passes through, so a build listed here is silent everywhere until
 * its users update.
 */
export function VersionBlocksTab() {
  const blocks = useQuery({
    queryKey: ['version-blocks'],
    queryFn: () => api.get<VersionBlock[]>('/api/notifications/version-blocks'),
  });

  return (
    <div className="space-y-4">
      <AddForm />

      <Card>
        <CardHeader
          title="Blocked builds"
          description="Checked on every send. Devices running one of these get nothing, whatever asks for it."
        />

        {blocks.isError && (
          <div className="p-4">
            <ErrorNote error={blocks.error} />
          </div>
        )}

        {blocks.isPending ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : blocks.data && blocks.data.length > 0 ? (
          <ul className="divide-y divide-line">
            {blocks.data.map((block) => (
              <BlockRow key={block.id} block={block} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<PackageX size={28} />}
            title="Every build can be notified"
            description="Add a build above to stop notifications reaching the devices running it."
          />
        )}
      </Card>
    </div>
  );
}

function AddForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [appKey, setAppKey] = useState<AppKey>('dualchat');
  const [platform, setPlatform] = useState<Platform>('android');
  const [versionName, setVersionName] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [reason, setReason] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post<VersionBlock>('/api/notifications/version-blocks', {
        app_key: appKey,
        platform,
        // The backend splits a pasted `1.4.0+31` itself, so either box works.
        version_name: versionName.trim(),
        version_code: versionCode.trim() || undefined,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Build silenced');
      setVersionName('');
      setVersionCode('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['version-blocks'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader
        title="Silence a build"
        description="Takes effect within a minute, on every send path at once."
      />
      <div className="flex flex-wrap items-end gap-2 p-4 sm:p-5">
        <Field label="App" className="min-w-[150px] flex-1">
          <Select
            value={appKey}
            onChange={(event) => setAppKey(event.target.value as AppKey)}
          >
            {APPS.map((app) => (
              <option key={app.key} value={app.key}>
                {app.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Platform" className="min-w-[130px] flex-1">
          <Select
            value={platform}
            onChange={(event) => setPlatform(event.target.value as Platform)}
          >
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </Select>
        </Field>
        <Field
          label="Version name"
          hint="Or paste the whole 1.4.0+31"
          className="min-w-[150px] flex-1"
        >
          <Input
            value={versionName}
            onChange={(event) => setVersionName(event.target.value)}
            placeholder="1.4.0"
          />
        </Field>
        <Field
          label="Version code"
          hint="Leave empty for every build of that version"
          className="min-w-[150px] flex-1"
        >
          <Input
            value={versionCode}
            onChange={(event) => setVersionCode(event.target.value)}
            placeholder="31"
          />
        </Field>
        <Field label="Reason" className="min-w-[180px] flex-1">
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Crashes on the payload"
          />
        </Field>
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          disabled={!versionName.trim()}
          loading={add.isPending}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </div>
    </Card>
  );
}

function BlockRow({ block }: { block: VersionBlock }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const build = block.version_code
    ? `${block.version_name}+${block.version_code}`
    : block.version_name;

  const remove = useMutation({
    mutationFn: () =>
      api.delete<void>(`/api/notifications/version-blocks/${block.id}`),
    onSuccess: () => {
      toast.success('Build can receive notifications again');
      void queryClient.invalidateQueries({ queryKey: ['version-blocks'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <li className="flex items-start justify-between gap-3 px-4 py-3 sm:px-5">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
          {APP_LABELS[block.app_key] ?? block.app_key}
          <Badge>{block.platform === 'ios' ? 'iOS' : 'Android'}</Badge>
          <span className="break-anywhere font-mono text-xs">{build}</span>
          {!block.version_code && <Badge tone="warn">all builds</Badge>}
        </p>
        <p className="mt-0.5 text-xs text-ink-3">
          {block.reason || 'No reason given'} · added{' '}
          {formatDateTime(block.created_at)}
        </p>
      </div>
      <Button
        variant="ghost"
        aria-label={`Unblock ${build}`}
        loading={remove.isPending}
        onClick={() => {
          if (confirm(`Let ${build} receive notifications again?`)) {
            remove.mutate();
          }
        }}
      >
        <Trash2 size={15} className="text-danger" />
      </Button>
    </li>
  );
}
